"use client";

import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Checkbox } from '@/src/components/ui/checkbox'
import { Input } from '@/src/components/ui/input'
import { Label } from '@/src/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/components/ui/select'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { ArrowCounterClockwiseIcon, TagIcon } from '@phosphor-icons/react'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { DeleteAction } from '@/src/components/manage/actions/DeleteAction'
import { FilterAction } from '@/src/components/manage/actions/FilterAction'
import { Pagination } from '@/src/components/manage/Pagination'
import { Badge } from '@/src/components/ui/badge'
import { useTranslation } from '@/src/i18n/client'
import { listAllOperations, deleteOperation } from '@/src/api/operation'
import { listAllOperationTypes } from '@/src/api/operation-type'
import { toast } from 'sonner'
import { DateTimeText } from '@/src/components/ui/date-time'
import { Button } from '@/src/components/ui/button';

const PAGE_SIZE = 20

export const Route = createFileRoute('/_manage/manage/admin/operations/list')({
  component: AdminOperationsPage,
})

interface OperationRow {
  id: string
  name: string
  provider?: string | null
  source?: string | null
  createdAt: string
  deletedAt?: string | null
  drawing: {
    id: string
    name?: string | null
    shareId?: string | null
    user?: { id: string; name?: string | null; email?: string | null } | null
  }
  operationType?: {
    id: string
    name: string
    labels?: { en?: string; de?: string } | null
    organizationId?: string | null
    organization?: { id: string; name: string } | null
  } | null
}

interface OperationTypeOption {
  id: string
  name: string
  labels?: { en?: string; de?: string } | null
  organizationId?: string | null
  organization?: { id: string; name: string } | null
}

function AdminOperationsPage() {
  const { t } = useTranslation('operation')
  const queryClient = useQueryClient()
  const [drawingId, setDrawingId] = useState('')
  const [userId, setUserId] = useState('')
  const [filterType, setFilterType] = useState<'drawing' | 'user' | 'none'>('none')
  const [operationTypeId, setOperationTypeId] = useState<string | null>('all')
  const [operationTypeScope, setOperationTypeScope] = useState<'all' | 'global' | 'organization'>('all')
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [page, setPage] = useState(0)
  const [selectedItems, setSelectedItems] = useState<string[]>([])

  const hasActiveFilters =
    !!drawingId ||
    !!userId ||
    includeDeleted ||
    operationTypeScope !== 'all' ||
    operationTypeId !== 'all'

  const { data: allOperationTypes = [] } = useQuery({
    queryKey: ['operationTypes', 'all', 'admin'],
    queryFn: () => listAllOperationTypes({ data: { includeDisabled: true } }),
  })

  const operationTypeOptions = (allOperationTypes as OperationTypeOption[]).filter((type) => {
    if (operationTypeScope === 'global') return !type.organizationId
    if (operationTypeScope === 'organization') return !!type.organizationId
    return true
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['operations', 'all', { drawingId, userId, operationTypeId, includeDeleted, page }],
    queryFn: () =>
      listAllOperations({
        data: {
          drawingId: drawingId || undefined,
          userId: userId || undefined,
          operationTypeId: operationTypeId !== 'all' ? operationTypeId : undefined,
          includeDeleted,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        },
      }),
  })

  const operations: OperationRow[] = (data?.operations ?? []) as OperationRow[]
  const total = data?.total ?? 0
  const pageCount = Math.ceil(total / PAGE_SIZE)

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteOperation({ data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operations', 'all'] })
    },
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => deleteOperation({ data: { id } })))
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed > 0) throw new Error(`Failed to delete ${failed} of ${ids.length} items`)
      return results
    },
    onSuccess: (_, ids) => {
      toast.success(`${ids.length} operation(s) deleted`)
      setSelectedItems([])
      queryClient.invalidateQueries({ queryKey: ['operations', 'all'] })
    },
    onError: () => {
      toast.error('Bulk delete failed')
    },
  })

  const columns: ColumnConfig<OperationRow>[] = [
    {
      key: 'name',
      label: t('operationName'),
      primary: true,
      sortable: false,
      render: (op) => (
        <div className="flex items-center gap-2">
          <span className={op.deletedAt ? 'font-medium text-muted-foreground line-through' : 'font-medium'}>{op.name}</span>
          {op.deletedAt && (
            <Badge variant="destructive" className="text-xs">Deleted</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'provider',
      label: t('provider'),
      width: 'w-36',
      render: (op) =>
        op.provider ? (
          <Badge variant="outline" className="text-xs">
            {op.provider}
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'source',
      label: t('source'),
      width: 'w-28',
      render: (op) =>
        op.source ? (
          <Badge variant="secondary" className="text-xs capitalize">
            {op.source}
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'operationType',
      label: 'Operation Type',
      width: 'w-56',
      render: (op) => {
        const type = op.operationType
        if (!type) {
          return <span className="text-muted-foreground">-</span>
        }
        const typeLabel = type.labels?.en || type.name
        const scopeLabel = type.organization?.name || 'Global'
        return (
          <div className="flex flex-col">
            <span className="truncate" title={typeLabel}>{typeLabel}</span>
            <span className="text-xs text-muted-foreground truncate" title={scopeLabel}>{scopeLabel}</span>
          </div>
        )
      },
    },
    {
      key: 'drawing',
      label: t('drawing'),
      width: 'w-48',
      truncate: true,
      render: (op) =>
        op.drawing ? (
          <Link
            to="/manage/admin/drawings/$id"
            params={{ id: op.drawing.id }}
            search={{ tab: 'data' }}
            className="text-primary hover:underline truncate block max-w-40"
            title={op.drawing.name ?? op.drawing.id}
          >
            {op.drawing.name || op.drawing.shareId || op.drawing.id}
          </Link>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'user',
      label: t('user'),
      width: 'w-40',
      truncate: true,
      render: (op) =>
        op.drawing?.user ? (
          <span className="truncate block max-w-36" title={op.drawing.user.name ?? op.drawing.user.email ?? op.drawing.user.id}>
            {op.drawing.user.name || op.drawing.user.email || op.drawing.user.id}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'createdAt',
      label: t('date'),
      width: 'w-36',
      sortable: false,
      render: (op) => <DateTimeText>{op.createdAt}</DateTimeText>,
    },
  ]

  function handleReset() {
    setDrawingId('')
    setUserId('')
    setFilterType('none')
    setOperationTypeId('all')
    setOperationTypeScope('all')
    setIncludeDeleted(false)
    setPage(0)
  }

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar selectedCount={selectedItems.length}>
        <AdminToolbar.BulkActions>
          <DeleteAction
            itemName={`${selectedItems.length} operation(s)`}
            itemType="operations"
            buttonLabel="Delete"
            showText={true}
            variant="destructive"
            size="lg"
            onDelete={async () => {
              await bulkDeleteMutation.mutateAsync(selectedItems)
            }}
            isPending={bulkDeleteMutation.isPending}
          />
        </AdminToolbar.BulkActions>
        <AdminToolbar.Right>
          <Pagination page={page} totalPages={pageCount} onPageChange={setPage} compact />
          <FilterAction
            className={
              hasActiveFilters
                ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 hover:text-primary-foreground'
                : ''
            }
          >
            <div className="flex flex-col gap-3">
              <Input
                placeholder={t('filterByDrawing')}
                value={filterType === 'drawing' ? drawingId : ''}
                onChange={(e) => {
                  setFilterType('drawing')
                  setDrawingId(e.target.value)
                  setUserId('')
                  setPage(0)
                }}
              />
              <Input
                placeholder={t('filterByUser')}
                value={filterType === 'user' ? userId : ''}
                onChange={(e) => {
                  setFilterType('user')
                  setUserId(e.target.value)
                  setDrawingId('')
                  setPage(0)
                }}
              />
              <Select
                value={operationTypeScope}
                onValueChange={(value) => {
                  setOperationTypeScope(value as 'all' | 'global' | 'organization')
                  setOperationTypeId('all')
                  setPage(0)
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Operation Type Scope" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Scopes</SelectItem>
                  <SelectItem value="global">Global</SelectItem>
                  <SelectItem value="organization">Organizations</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={operationTypeId}
                onValueChange={(value) => {
                  setOperationTypeId(value)
                  setPage(0)
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by operation type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Operation Types</SelectItem>
                  {operationTypeOptions.map((type) => {
                    const typeLabel = type.labels?.en || type.name
                    const scopeLabel = type.organization?.name || 'Global'
                    return (
                      <SelectItem key={type.id} value={type.id}>
                        {typeLabel} · {scopeLabel}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="admin-ops-include-deleted"
                    checked={includeDeleted}
                    onCheckedChange={(checked) => {
                      setIncludeDeleted(!!checked)
                      setPage(0)
                    }}
                  />
                  <Label htmlFor="admin-ops-include-deleted" className="text-xs cursor-pointer">
                    Show deleted
                  </Label>
                </div>
                <Button variant="ghost" size="icon-sm" onClick={handleReset} aria-label="Reset filters">
                  <ArrowCounterClockwiseIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </FilterAction>
        </AdminToolbar.Right>
      </AdminToolbar>

      <div className="mt-4">
        <DataTable<OperationRow>
          data={operations}
          columns={columns}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : undefined}
          emptyMessage={t('noOperationsFound')}
          selectable={true}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          actions={(op) => (
            op.deletedAt ? null : (
              <>
                <EditAction
                  to="/manage/operations/$operationId/edit"
                  params={{ operationId: op.id }}
                />
                <DeleteAction
                  itemName={op.name}
                  itemType="operation"
                  onDelete={async () => {
                    await deleteMutation.mutateAsync(op.id)
                  }}
                  isPending={deleteMutation.isPending}
                />
              </>
            )
          )}
        />
      </div>

    </div>
  )
}
